import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { FormService } from '../organizations/features/booking/components/form/service';
import { CommonModule, DatePipe } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { DateAdapter, provideNativeDateAdapter, NativeDateAdapter } from '@angular/material/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { setHours, setMinutes } from 'date-fns';
import { DateLocaleService } from '../../services/date-locale.service';

export class CustomDateAdapter extends NativeDateAdapter {
  constructor(private dateLocaleService: DateLocaleService) {
    super();
  }

  override getFirstDayOfWeek(): number {
    return this.dateLocaleService.getFirstDayOfWeek();
  }
}

@Component({
  selector: 'app-datepicker-modal',
  imports: [
    MatDatepickerModule,
    DatePipe,
    MatMenuModule,
    TranslocoModule,
    CommonModule
  ],
  providers: [
    provideNativeDateAdapter(),
    DatePipe,
    DateLocaleService,
    {
      provide: DateAdapter,
      useFactory: (dateLocaleService: DateLocaleService) => new CustomDateAdapter(dateLocaleService),
      deps: [DateLocaleService]
    }
  ],
  templateUrl: './datepicker-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './datepicker-modal.component.scss'
})
export class DatepickerModalComponent {

  minDate: Date = new Date();
  maxDate: Date | null = null;
  selectedDate: Date | null = null;
  selectedTime: Date = new Date();
  timeSelector: boolean = false;

  selectedHour: string | null = null;
  selectedMinute: string | null = null;

  currentTab: string = 'date';

  type: string | null = null;
  @Output() dateSelectEvent = new EventEmitter();
  @Output() timeSelectEvent = new EventEmitter();

  subscription: any;
  locale: string = "";

  hours: string[] = [];
  minutes: string[] = ['00', '15', '30', '45'];
  minutesArrival: string[] = ['10', '30', '50'];
  restrictedModalMinutes: string[] = ['00', '30'];
  filteredHours: string[] = [];
  filteredMinutes: string[] = [];

  constructor(
    private _adapter: DateAdapter<any>,
    private translocoService: TranslocoService,
    private dateLocaleService: DateLocaleService,
    public dialogRef: MatDialogRef<DatepickerModalComponent>,
    public datepipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) public data: {
      form: FormService,
      type: string,
      selectedDate: Date | null,
      minDate: Date | null,
      isHotel: boolean,
      hotelDate: Date | null,
      timeSelector: boolean,
      modalRestriction?: boolean
    },
  ) {
    this.type = data.type;
    this.timeSelector = data.timeSelector;
    this.hours = Array.from({ length: 24 }, (_, i) => (i < 10 ? '0' + i : '' + i));
    this.filterTimeOptions();
    this.setDefaultTime();
    this.subscription = this.translocoService.langChanges$.subscribe((lang) => {
      this.locale = lang;
      const adapterLocale = lang === 'sv' ? 'sv-SE' : 'en-GB';
      this._adapter.setLocale(adapterLocale);
    });

    // Initialize locale immediately
    const currentLang = this.translocoService.getActiveLang();
    this.locale = currentLang;
    const initialAdapterLocale = currentLang === 'sv' ? 'sv-SE' : 'en-GB';
    this._adapter.setLocale(initialAdapterLocale);

    //@PAC This logic sets min/maxdate for the picker for "flygets", should probably be moved outside the component
    if (this.type === 'arrival') {
      const hotels = this.data.form.getForm().get('hotels');
      const search = this.data.form.getForm().get('search');
      // if a minDate was explicitly provided from the opener, use it
      if (this.data.minDate) {
        this.minDate = new Date(this.data.minDate);
      } else {
        if (search.controls.isDepartureHotel && search.controls.isDepartureHotel.value && hotels.controls.departureDatetime && hotels.controls.departureDatetime.value) {
          this.minDate = new Date(hotels.controls.departureDatetime.value);
        } else if (search.controls.departureFlightDate && search.controls.departureFlightDate.value) {
          this.minDate = new Date(search.controls.departureFlightDate.value);
        }
        if (data.isHotel && data.hotelDate) {
          this.maxDate = new Date(data.hotelDate);
        }
      }
    }

    if (this.type === 'departure') {
      if (data.isHotel && data.hotelDate) {
        this.minDate = new Date(data.hotelDate);
      } else {
        if (this.data.minDate) {
          this.minDate = new Date(this.data.minDate);
        }
      }
    }

    if (!data.selectedDate && this.timeSelector) {
      this.selectedHour = null;
      this.selectedMinute = null;
      this.selectedTime = new Date();
      if (this.type == "arrival") {
        this.selectedTime.setHours(12, 10, 0, 0);
      } else {
        this.selectedTime.setHours(12, 0, 0, 0);
      }
    } else {
      this.selectedDate = data.selectedDate;
      this.selectedTime = data.selectedDate!;
      this.selectedHour = null;
      this.selectedMinute = null;
    }
  }

  setDefaultTime() {
    if (this.timeSelector) {
      return;
    }
    if (this.filteredHours.length > 0) {
      const middleIndex = Math.floor(this.filteredHours.length / 2);
      const middleHour = parseInt(this.filteredHours[middleIndex]);
      const firstMinute = this.filteredMinutes.length > 0 ? parseInt(this.filteredMinutes[0]) : 0;

      this.selectedTime = setHours(this.selectedTime, middleHour);
      this.selectedTime = setMinutes(this.selectedTime, firstMinute);
    }
  }

  filterTimeOptions() {
    const now = new Date((new Date())?.toLocaleString("en-US", { timeZone: "Europe/Stockholm" }));
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // If selected date is today, filter times
    const isToday = this.selectedDate ?
      this.selectedDate.toDateString() === now.toDateString() :
      (this.selectedTime ? this.selectedTime.toDateString() === now.toDateString() : false);

    if (isToday) {
      // Round up to next 30-minute slot (10:05 -> 10:30, 10:35 -> 11:00)
      const nextSlotMinute = currentMinute <= 30 ? 30 : 0;
      const nextSlotHour = currentMinute > 30 ? currentHour + 1 : currentHour;

      // Filter hours - only show from next slot hour onwards
      this.filteredHours = this.hours.filter(h => parseInt(h) >= nextSlotHour);

      // Filter minutes based on selected hour
      const currentH = this.selectedHour ? parseInt(this.selectedHour, 10) : -1;
      this.updateFilteredMinutes(currentH, nextSlotHour, nextSlotMinute);
    } else {
      this.filteredHours = this.hours;
      this.filteredMinutes = this.getMinutesArray();
    }
  }

  updateFilteredMinutes(selectedHour: number | null, nextSlotHour: number, nextSlotMinute: number) {
    const minutesArray = this.getMinutesArray();
    const now = new Date((new Date())?.toLocaleString("en-US", { timeZone: "Europe/Stockholm" }));
    const isToday = this.selectedDate ?
      this.selectedDate.toDateString() === now.toDateString() :
      (this.selectedTime ? this.selectedTime.toDateString() === now.toDateString() : false);

    if (isToday && selectedHour === nextSlotHour) {
      // For the current hour, only show minutes >= next slot minute
      this.filteredMinutes = minutesArray.filter(m => parseInt(m) >= nextSlotMinute);
    } else {
      this.filteredMinutes = minutesArray;
    }
  }

  getMinutesArray(): string[] {
    if (this.data.modalRestriction) {
      return this.restrictedModalMinutes;
    } else if (this.type === 'arrival') {
      return this.minutesArrival;
    } else {
      return this.minutes;
    }
  }

  changeTab(tab: string) {
    this.currentTab = tab;
    this.filterTimeOptions();
    if (this.selectedHour !== null && this.selectedMinute !== null) {
      this.timeSelectEvent.emit(this.selectedTime);
    }
  }

  changeHour(event: any) {
    this.selectedHour = event;
    const newHour = parseInt(event, 10);

    const now = new Date((new Date())?.toLocaleString("en-US", { timeZone: "Europe/Stockholm" }));
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const nextSlotMinute = currentMinute <= 30 ? 30 : 0;
    const nextSlotHour = currentMinute > 30 ? currentHour + 1 : currentHour;

    this.updateFilteredMinutes(newHour, nextSlotHour, nextSlotMinute);

    if (this.selectedMinute !== null) {
      const newMinute = parseInt(this.selectedMinute, 10);
      this.selectedTime = setMinutes(setHours(this.selectedDate || new Date(), newHour), newMinute);
      this.timeSelectEvent.emit(this.selectedTime);
    }
  }

  changeMinute(event: any) {
    this.selectedMinute = event;
    const newMinute = parseInt(event, 10);

    if (this.selectedHour !== null) {
      const newHour = parseInt(this.selectedHour, 10);
      this.selectedTime = setMinutes(setHours(this.selectedDate || new Date(), newHour), newMinute);
      this.timeSelectEvent.emit(this.selectedTime);
    }
  }

  ngAfterContentInit(): void {
    //this.dateSelectEvent.emit(this.selectedDate); 
  }

  selectDate(date: any) {
    this.selectedDate = date;
    this.filterTimeOptions();
    this.dateSelectEvent.emit(date);

    if (this.selectedHour !== null && this.selectedMinute !== null) {
      const hourNum = parseInt(this.selectedHour, 10);
      const minNum = parseInt(this.selectedMinute, 10);
      this.selectedTime = setMinutes(setHours(new Date(date), hourNum), minNum);
      this.timeSelectEvent.emit(this.selectedTime);
    }

    if (!this.timeSelector) {
      this.dialogRef.close();
    }
  }

  isDoneDisabled(): boolean {
    if (!this.timeSelector) {
      return !this.selectedDate;
    }
    return this.selectedHour === null || this.selectedMinute === null;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

}
